import type { OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewEncapsulation,
  output,
  inject,
  input,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import kalendae from 'kalendae/build/kalendae';
import moment from 'moment-timezone';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RtInput } from '@core/user-interface-legacy';

const Kalendae = kalendae.bind(kalendae.prototype);
Kalendae.moment = moment;

export type DatePickerDirection = 'any' | 'past' | 'today-past' | 'today-future' | 'future';

// Value prop:
// - In "single" mode, `value` is a YYYY-MM-DD string.
// - In "multiple" mode, `value` is an array of multiple dates.
// - In "range" mode, `value` is an array of two dates.
type Handle = [string, (value: string) => void];
class KalendaeType {
  public draw: () => void;
  public getSelectedRaw: () => moment.Moment[];
  public setSelected: (value: string | string[], flag: boolean) => void;
  public subscribe: <T>(event: string, callback: (value: T) => void) => Handle;
  public unsubscribe: (handle: Handle) => void;
  public container: HTMLElement;
  public settings: {
    dateClassMap: { [key: string]: string };
    weekStart: 0 | 1;
  };
}

// TODO make sure the focus and blur events are working correctly, or get rid of them, if we can't rely on them
@Component({
  selector: 'rt-date-picker',
  template: '',
  styleUrls: ['./date-picker.component.less'],
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.kalendae-has-no-padding]': 'hasNoPadding()',
    '(focus)': 'focus.emit($event)',
    '(blur)': 'blur.emit($event)',
  },
  standalone: true,
})
export class DatePickerComponent
  extends RtInput<string | string[]>
  implements OnDestroy, OnChanges
{
  private readonly elementRef = inject(ElementRef);

  // If it's set to true then today's date will be applied as "selected"
  public readonly selectNowByDefault = input<boolean>(true);

  // Allow clicks on days that fall outside of the currently focused month
  public readonly dayOutOfMonthClickable = input<boolean>(false);

  // Dates to be disallowed from selection
  public readonly blackout = input<((date: any) => boolean) | null>(null);

  // the number of calendar view
  public readonly months = input<number>(1);

  // single | range | multiple
  public readonly mode = input<'single' | 'range' | 'multiple'>('single');

  // accepted values: past, today-past, any, today-future, future
  public readonly direction = input<DatePickerDirection>('any');

  // A key/value collection of css classes organized by date
  public readonly dateClassMap = input<{
    [key: string]: string;
  }>(undefined!);

  public readonly useYearNav = input<boolean>(true);

  public readonly hasNoPadding = input<boolean>(false);

  public readonly viewStartDate = input<string>(undefined!);

  public monthChange = output<string>();
  /**
   * Temp
   * Emits after value changed in reactive forms
   */
  public valueChanged = output<string | string[]>();

  private destroyPicker$ = new Subject<void>();
  private datepickerInstance?: KalendaeType;
  private duringDatepickerValueChange: boolean = false;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);
    const ngControl = inject(NgControl, { optional: true, self: true }) ?? undefined;

    super(cdRef, ngControl);
  }

  public ngOnInit(): void {
    this.destroyPicker$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.destroyPickerInstance());
    this.setPickerInstance();
  }

  public ngOnDestroy(): void {
    this.destroyPicker$.next();
    this.destroyPicker$.complete();
    super.ngOnDestroy();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.datepickerInstance && changes.value) {
      if (changes.value.previousValue !== changes.value.currentValue) {
        this.setDatepickerValue(changes.value.currentValue);
      }
    }

    if (this.datepickerInstance && changes.dateClassMap) {
      if (changes.dateClassMap.previousValue !== changes.dateClassMap.currentValue) {
        this.datepickerInstance.settings.dateClassMap = changes.dateClassMap.currentValue;
        this.datepickerInstance.draw();
      }
    }

    if (this.datepickerInstance && changes.weekStartDay) {
      if (changes.weekStartDay.previousValue !== changes.weekStartDay.currentValue) {
        // Because Kalendlee sets header nodes when it's initialized
        // and it skips it in the redraw function we can't change weekStartDay dynamically
        this.destroyPickerInstance();
        this.setPickerInstance();
      }
    }
  }

  protected setValue(value: string | string[]): void {
    this.value = value;
    this.setDatepickerValue(value);
  }

  private initDatepicker(): KalendaeType {
    let value: string | string[] | moment.Moment | moment.Moment[] | null = this.value;
    if (!value && this.selectNowByDefault()) {
      value = this.mode() === 'single' ? moment() : [moment()];
    } else if (Array.isArray(value)) {
      value = value.map(v => {
        if (typeof v === 'string' && v.length === 10) {
          return moment(v, 'YYYY-MM-DD');
        } else {
          // eslint-disable-next-line no-console
          (console.warn || console.log)(
            `Unrecognized format of "value" prop passed to rt-date-picker: "${v}". It should be either string like "YYYY-MM-DD" or null.`,
          );
          return moment(v);
        }
      });
    } else if (typeof value === 'string' && value.length === 10) {
      value = moment(value, 'YYYY-MM-DD');
    } else if (value) {
      // eslint-disable-next-line no-console
      (console.warn || console.log)(
        `Unrecognized format of "value" prop passed to rt-date-picker: "${value}". It should be either string like "YYYY-MM-DD" or null.`,
      );
      value = moment(value);
    } else {
      value = null;
    }

    const mode = this.mode();
    if (value && mode === 'single' && Array.isArray(value)) {
      throw new Error(
        "DatePickerComponent.value has to be a date when DatePickerComponent.mode === 'single'",
      );
    }
    if (value && mode !== 'single' && !Array.isArray(value)) {
      throw new Error(
        "DatePickerComponent.value has to be an array when DatePickerComponent.mode !== 'single'",
      );
    }

    const dateClassMap = this.dateClassMap();
    const viewStartDate = this.viewStartDate();
    return new Kalendae({
      attachTo: this.elementRef.nativeElement,
      months: this.months(),
      mode: mode,
      weekStart: moment.localeData().firstDayOfWeek(),
      direction: this.direction(),
      blackout: this.blackout(),
      dayOutOfMonthClickable: this.dayOutOfMonthClickable(),
      selected: value,
      useYearNav: this.useYearNav(),
      ...(dateClassMap ? { dateClassMap: dateClassMap } : {}),
      ...(viewStartDate ? { viewStartDate: viewStartDate } : {}),
    });
  }

  private listenToDatepickerChanges(datepickerInstance: KalendaeType): void {
    this.kalendaeSubscribe(datepickerInstance, 'change')
      .pipe(takeUntil(this.destroyPicker$))
      .subscribe(() => {
        if (this.duringDatepickerValueChange) {
          return;
        }
        const selectedDates = datepickerInstance.getSelectedRaw().map(m => m.format('YYYY-MM-DD'));
        const nextValue =
          this.mode() === 'single'
            ? selectedDates.length > 0
              ? selectedDates[0]
              : null
            : selectedDates;
        this.handleChange(nextValue!);
        try {
          this.valueChanged.emit(nextValue!);
        } catch (e) {
          /**
           * this component can be destroyed after handleChange like
           * (valueChanges)="onValueChange()"
           * public onValueChange() {
           *   this.dropdownRef.open = false;
           *   this.cdRef.detectChanges();
           * }
           * so this.valueChanged.emit(nextValue!); can throw an error
           */
        }
      });

    this.kalendaeSubscribe<string>(datepickerInstance, 'view-changed')
      .pipe(takeUntil(this.destroyPicker$))
      .subscribe(month => this.monthChange.emit(month));
  }

  private setDatepickerValue(value: string | string[]): void {
    if (this.datepickerInstance) {
      try {
        this.duringDatepickerValueChange = true;
        this.datepickerInstance.setSelected(value, true);
      } catch (e) {
        console.error(e);
      } finally {
        this.duringDatepickerValueChange = false;
      }
    }
  }

  private destroyPickerInstance(): void {
    if (this.datepickerInstance) {
      if (this.datepickerInstance.container && this.datepickerInstance.container.parentNode) {
        this.datepickerInstance.container.parentNode.removeChild(this.datepickerInstance.container);
      }
      delete this.datepickerInstance;
    }
  }

  private setPickerInstance(): void {
    this.datepickerInstance = this.initDatepicker();

    if (this.datepickerInstance) {
      this.listenToDatepickerChanges(this.datepickerInstance);
    }
  }

  private kalendaeSubscribe<T>(
    instance: KalendaeType,
    event: Parameters<KalendaeType['subscribe']>[0],
  ) {
    return new Observable<T>(observer => {
      const handle = instance.subscribe<T>(event, value => {
        observer.next(value);
      });

      return () => instance.unsubscribe(handle);
    });
  }
}
